import { Tabs, Text, Tooltip } from "@shopify/polaris"
import classNames from "classnames"
import { observer } from "mobx-react"
import React from "react"

import {
  FormatPriceText,
  SimpleTableList,
  SpinnerContainer,
} from "@/components"
import CasingPly from "@/components/CasingPly/CasingPly"
import DynamicLoadError from "@/components/DynamicLoadError/DynamicLoadError"
import STableStyles from "@/components/SimpleTableList/index.module.scss"
import { SimpleTableListFields } from "@/components/SimpleTableList/types"
import stores from "@/stores"
import { autoReplenishPoint } from "@/utils/formatNumber"

import { ArrowDiagonalIcon, ArrowDownIcon } from "./Icon"
import styles from "./index.module.scss"

// 声明全局 t 函数类型
declare global {
  interface Window {
    t: (key: string) => string
  }
}

const t = (key: string) => window.t(key)

type IProps = any

interface State {
  tabs: Array<{
    id: string
    content: string
    panelID: string
    tooltip: string
    indexVal: string
  }>
  selected: number
}

const DashboardLineChart = React.lazy(
  () => import("@/components/Charts/DashboardLineChart"),
)

@observer
class ViewsNumberCharts extends React.Component<IProps, State> {
  constructor(props: IProps) {
    super(props)

    this.state = {
      tabs: [
        {
          id: "total-sales-of-free-return-service",
          content: t("FreeReturnServiceSales"),
          panelID: "insuranceValue",
          tooltip: t("FreeReturnServiceSalesGeneratedByP"),
          indexVal: "total_insurance_value",
        },
        {
          id: "label-fee",
          content: t("ShippingFee"),
          panelID: "labelFee",
          tooltip: t("LableFeeActuallyChargedWithinTheSe"),
          indexVal: "label_fee",
        },
        {
          id: "return-fee",
          content: t("RestockingFee"),
          panelID: "returnFee",
          tooltip: t("RestockingFeeActuallyChargedWithinT"),
          indexVal: "return_fee",
        },
        {
          id: "return-rewards",
          content: t("ReturnRewards"),
          panelID: "returnRewards",
          tooltip: t("ReturnRewardsActuallyIssuedWithinTh"),
          indexVal: "return_rewards",
        },
        {
          id: "shipping-cost",
          content: t("ReturnShippingCost"),
          panelID: "shippingCost",
          tooltip: t("ReturnShippingCostDirectIntegration"),
          indexVal: "total_ship_amount",
        },
      ],
      selected: 0,
    }
  }

  public componentDidMount() {}

  handleTabChange = (index: number) => {
    this.setState({ selected: index })
  }

  // 展示统计数据
  showStatisticsCount = (selected: number, indexVal: string) => {
    // 直接从 stores 获取最新数据，确保实时更新
    const {
      ReturnRequestsDataStore: {
        total_return_fee,
        total_return_rewards,
        total_label_fee,
        total_ship_amount,
        compare_rate,
        total_insurance_value,
        hasCompare,
      },
      UserInfoStore,
    } = stores

    const countMap = {
      0: total_insurance_value,
      1: total_label_fee,
      2: total_return_fee,
      3: total_return_rewards,
      4: total_ship_amount,
    }

    return (
      <div className="flex items-center gap-2">
        <span
          style={
            { "--p-text-body-sm-font-weight": "600" } as React.CSSProperties
          }
        >
          <Text variant="bodyMd" as="span">
            <span className="text-[#303030]">
              {countMap[selected] ? (
                <FormatPriceText
                  value={countMap[selected]}
                  currency={UserInfoStore.data.currency_format}
                />
              ) : (
                <FormatPriceText
                  value={0}
                  currency={UserInfoStore.data.currency_format}
                />
              )}
            </span>
          </Text>
        </span>

        {compare_rate && compare_rate[indexVal] !== 0 && hasCompare ? (
          <div className="flex items-center gap-1">
            {compare_rate[indexVal] > 0 ? (
              <ArrowDiagonalIcon />
            ) : (
              <ArrowDownIcon />
            )}
            {compare_rate[indexVal] > 0 ? (
              <Text as="span" variant="bodyXs" tone="subdued">
                {Math.round(compare_rate[indexVal])}%
              </Text>
            ) : (
              <Text as="span" variant="bodyXs" tone="subdued">
                {Math.round(compare_rate[indexVal])
                  ?.toString()
                  ?.replace(/^-/, "")}
                %
              </Text>
            )}
          </div>
        ) : null}
      </div>
    )
  }

  render() {
    const { ReturnRequestsDataStore } = stores
    const { tabs, selected } = this.state

    const loading = ReturnRequestsDataStore.loading.overviewLoading

    // 动态生成带有统计数据的 tab 内容
    const tabsWithContent = tabs.map((tab, index) => ({
      ...tab,
      content: (
        <div className="flex flex-col items-start gap-2">
          <Tooltip content={tab.tooltip} preferredPosition="above">
            <span
              style={{
                borderBottom: "0.125rem dotted var(--p-color-border-tertiary)",
                paddingBottom: "2px",
                "--p-text-heading-sm-font-weight": "600",
              }}
              className="text-[#303030]"
            >
              <Text as="span" variant="headingSm">
                {tab.content}
              </Text>
            </span>
          </Tooltip>
          <p>{this.showStatisticsCount(index, tab.indexVal)}</p>
        </div>
      ),
    })) as any // 使用类型断言绕过 Polaris Tabs 的类型限制

    return (
      <div
        className={classNames("Polaris-Card", styles.AnalysisContainer)}
        style={
          {
            "--p-action-primary": "#84CAE9",
            "--p-surface-primary-selected-pressed": "#999EA4",
          } as React.CSSProperties
        }
      >
        <Tabs
          tabs={tabsWithContent}
          selected={selected}
          onSelect={this.handleTabChange}
        >
          <CasingPly show={loading}>{this.dataTable()}</CasingPly>
          <SpinnerContainer
            loading={loading}
            spinnerClassName={["relative", "-top-20"]}
          >
            <DynamicLoadError errorMsg={t("NetworkErrorUnableToLoadCharts")}>
              <React.Suspense
                fallback={
                  <SpinnerContainer loading={!loading}>
                    <div className={styles.CardSection__ReactECharts} />
                  </SpinnerContainer>
                }
              >
                <DashboardLineChart
                  className={styles.CardSection__ReactECharts}
                  option={
                    ReturnRequestsDataStore?.chartOptions[
                      tabs[selected].panelID
                    ]
                  }
                />
              </React.Suspense>
            </DynamicLoadError>
          </SpinnerContainer>
        </Tabs>
      </div>
    )
  }

  dataTable() {
    const fields: SimpleTableListFields[] = [
      { field: "title", title: "title" },
      { className: STableStyles.AlignRight, field: "val", title: "Val" },
    ]
    const data: any[] = [
      // {
      //   title: "Conversion rate",
      //   val: conversion_rate === "0%" ? "-" : conversion_rate
      // }
    ]

    return <SimpleTableList data={data} fields={fields} noHead />
  }
}

export default ViewsNumberCharts
